import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './Dashboard.css';

/**
 * PendingPage
 * Elenca le transazioni in attesa di approvazione per il tenant dell'utente.
 * Visibile/operativa per ruoli: 'segretario' o 'tesoriere'.
 */
const API_BASE = (process.env.REACT_APP_API_BASE || '').replace(/\/$/, '');

const getUserRole = (user) => {
  const role = user?.role ?? user?.ruolo ?? user?.profile?.role ?? user?.profile?.ruolo;
  return typeof role === 'string' ? role.trim().toLowerCase() : '';
};

const canApproveByRole = (user) => {
  const role = getUserRole(user);
  return role === 'segretario' || role === 'tesoriere';
};

const parseAmount = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;

  if (typeof value === 'string') {
    // Supporto base per formati IT: "1.234,56"
    const normalized = value.replace(/\./g, '').replace(',', '.').trim();
    const n = Number(normalized);
    return Number.isFinite(n) ? n : 0;
  }

  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

function PendingPage({ token, user }) {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [approvingIds, setApprovingIds] = useState(() => new Set());

  // Serve per abortire fetch pendenti quando cambia token o il componente si smonta
  const abortRef = useRef(null);

  const allowed = useMemo(() => canApproveByRole(user), [user]);

  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat('it-IT', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    []
  );

  const dateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat('it-IT', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }),
    []
  );

  const formatDate = useCallback(
    (value) => {
      if (!value) return '—';
      const d = value instanceof Date ? value : new Date(value);
      if (Number.isNaN(d.getTime())) return String(value);
      return dateFormatter.format(d);
    },
    [dateFormatter]
  );

  /**
   * Wrapper fetch: controlla res.ok, gestisce JSON/text e ritorna payload o lancia errore.
   */
  const apiRequest = useCallback(
    async (path, options = {}) => {
      if (!token) throw new Error('Sessione non valida: token mancante.');

      const res = await fetch(`${API_BASE}${path}`, {
        ...options,
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
          ...(options.headers || {}),
        },
        signal: options.signal,
      });

      const contentType = res.headers.get('content-type') || '';
      const isJson = contentType.includes('application/json');

      let payload = null;
      try {
        payload = isJson ? await res.json() : await res.text();
      } catch {
        payload = null;
      }

      if (!res.ok) {
        const serverMessage =
          payload && typeof payload === 'object'
            ? payload.message || payload.error
            : typeof payload === 'string'
              ? payload
              : null;

        throw new Error(serverMessage || `Richiesta fallita (HTTP ${res.status}).`);
      }

      return payload;
    },
    [token]
  );

  const fetchPending = useCallback(async () => {
    if (!token) return;

    // abort precedente (se c’è)
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError('');
    setNotice('');

    try {
      const data = await apiRequest('/api/transactions/pending', {
        signal: controller.signal,
      });

      if (!Array.isArray(data)) {
        throw new Error('Risposta inattesa dal server (atteso un array).');
      }

      setTransactions(data);
    } catch (err) {
      if (err?.name === 'AbortError') return; // richieste annullate: nessun errore UI
      setError(err?.message || 'Errore nel recupero delle transazioni pendenti.');
    } finally {
      setLoading(false);
    }
  }, [apiRequest, token]);

  useEffect(() => {
    if (token && allowed) {
      fetchPending();
    } else {
      // reset stato se non loggato/non autorizzato
      setTransactions([]);
      setLoading(false);
      setError('');
      setNotice('');
    }

    return () => {
      abortRef.current?.abort();
    };
  }, [token, allowed, fetchPending]);

  // auto-hide del messaggio di successo
  useEffect(() => {
    if (!notice) return;
    const t = window.setTimeout(() => setNotice(''), 3000);
    return () => window.clearTimeout(t);
  }, [notice]);

  const handleApprove = useCallback(
    async (txId) => {
      if (!allowed || !txId) return;

      // Stato approvazione per singola riga
      setApprovingIds((prev) => {
        const next = new Set(prev);
        next.add(txId);
        return next;
      });

      setError('');
      setNotice('');

      try {
        await apiRequest(`/api/transactions/${encodeURIComponent(txId)}/approve`, {
          method: 'POST',
        });

        setNotice('Transazione approvata con successo.');

        // Aggiornamento UI immediato (ottimistico)
        setTransactions((prev) => prev.filter((t) => t?.id !== txId));

        // Risincronizzo con il server (per sicurezza)
        fetchPending();
      } catch (err) {
        setError(err?.message || "Errore durante l'approvazione.");
      } finally {
        setApprovingIds((prev) => {
          const next = new Set(prev);
          next.delete(txId);
          return next;
        });
      }
    },
    [allowed, apiRequest, fetchPending]
  );

  // UI stati base
  if (!token) {
    return (
      <div className="pending-page">
        <h2>Transazioni in Attesa</h2>
        <p>Effettua il login per visualizzare questa pagina.</p>
      </div>
    );
  }

  if (!allowed) {
    return (
      <div className="pending-page">
        <h2>Transazioni in Attesa</h2>
        <p className="error">
          Non hai i permessi per approvare le transazioni (ruolo richiesto: segretario o tesoriere).
        </p>
      </div>
    );
  }

  return (
    <div className="pending-page" aria-busy={loading}>
      <div className="pending-toolbar">
        <h2>Transazioni in Attesa</h2>

        <button
          type="button"
          className="open-modal-btn"
          onClick={fetchPending}
          disabled={loading}
          aria-label="Aggiorna transazioni in attesa"
          title="Aggiorna"
        >
          {loading ? 'Aggiornamento…' : 'Aggiorna'}
        </button>
      </div>

      {error && <p className="error" role="alert">{error}</p>}
      {notice && <p className="success" role="status">{notice}</p>}

      {loading ? (
        <p>Caricamento...</p>
      ) : transactions.length === 0 ? (
        <p>Nessuna transazione in attesa</p>
      ) : (
        <div className="table-wrapper">
          <table className="transaction-table">
            <thead>
              <tr>
                <th scope="col">Data</th>
                <th scope="col">Descrizione</th>
                <th scope="col" className="right">Importo</th>
                <th scope="col">Metodo</th>
                <th scope="col" className="right">Azioni</th>
              </tr>
            </thead>

            <tbody>
              {transactions.map((t) => {
                const amount = parseAmount(t?.importo);
                const isApproving = approvingIds.has(t?.id);

                return (
                  <tr
                    key={t?.id ?? `${t?.data}-${t?.descrizione}-${t?.importo}`}
                    className={amount < 0 ? 'negative' : 'positive'}
                  >
                    <td>{formatDate(t?.data)}</td>
                    <td className="description">{t?.descrizione || '—'}</td>
                    <td className="right amount">{currencyFormatter.format(amount)}</td>
                    <td>{t?.paymentMethod || '—'}</td>
                    <td className="right">
                      <button
                        type="button"
                        className="open-modal-btn"
                        onClick={() => handleApprove(t?.id)}
                        disabled={!t?.id || isApproving}
                        aria-label={`Approva transazione ${t?.descrizione || ''}`.trim()}
                      >
                        {isApproving ? 'Approvazione…' : 'Approva'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default PendingPage;
